import * as Sentry from "@sentry/react";

import { toaster } from "@/web/components/primitives/toast";
import { logger } from "@/web/lib/logging";
import { errorMessage } from "@/web/lib/spacetimedbErrors";
import { truncate } from "@/web/lib/strings";

const MAX_CLIENT_MESSAGE_LENGTH = 100;

/**
 * Centralized error handling and reporting utility.
 *
 * Use `reportError` to log the error and send it to Sentry.
 * Use `logError` to only log the error (no Sentry).
 * Use `default` for both reporting (logging + Sentry) and showing a user-facing toast notification.
 */
export class ErrorHandler {
  /**
   * Report the error by logging it and sending it to Sentry for further analysis.
   * @param error - The error to handle.
   * @returns The normalized Error object.
   */
  static reportError = (error: unknown) => {
    const message = errorMessage(error);
    const normalizedError = error instanceof Error ? error : new Error(message || String(error));
    logger.withError("Error handler", normalizedError);
    Sentry.captureException(normalizedError);
    return normalizedError;
  };

  /**
   * Log the error for debugging purposes, but do NOT send it to Sentry.
   * @param error - The error to handle.
   * @returns The normalized Error object.
   */
  static logError = (error: unknown) => {
    const message = errorMessage(error);
    const normalizedError = error instanceof Error ? error : new Error(message || String(error));
    logger.withError("Error handler", normalizedError);
    return normalizedError;
  };

  /**
   * Default error handler for client mutations/actions.
   * Logs and reports the error, then displays a user-facing toast with a human-readable message.
   * Surfaces the real server message if possible, otherwise falls back to a generic error.
   * @param error - The error to handle.
   */
  static default = (error: unknown) => {
    ErrorHandler.reportError(error);

    // Toast the NORMALIZED message, not the raw `Error.message`. `errorMessage`
    // strips the SpacetimeDB host's "The module instance encountered a fatal
    // error:" narration from in front of our own `SenderError` copy; reading
    // `.message` off the error object skipped that whenever the SDK rejected with
    // a real `Error` rather than a bare string. Sentry still receives the
    // untouched original via `reportError`.
    const message = errorMessage(error);
    toaster({
      message: message ? truncate(message, MAX_CLIENT_MESSAGE_LENGTH) : "Unexpected error occurred",
      variant: "error",
    });
  };
}
